import { useEffect, useMemo, useRef } from 'react';
import equal from 'deep-equal';
import { getSavedSearchParams, useMultipleStateDebounce, useSaveFilters } from '../hooks';

type PropsType = { [key: string]: any };

const getFilteredFromStates = (states: PropsType, defaultStates: PropsType): PropsType => {
  const result: PropsType = {};
  Object.keys(states).forEach((key) => {
    if (equal(defaultStates[key], states[key])) return;
    result[key] = states[key];
  });
  return result;
};

export const useStatesWithFilterCounter = (
  defaultStates: PropsType,
  importantStates: any = {},
  delay?: number,
  saveFilters = true,
): [PropsType, (key: string, value?: any, isEmptyFilter?: boolean) => void, PropsType] => {
  const defaultStatesRef = useRef(defaultStates);
  const [oldFilters, setOldFilters] = useSaveFilters('table', saveFilters, {
    ...importantStates,
    ...getSavedSearchParams(defaultStates),
  });
  const initialStates = { ...defaultStates, ...oldFilters };
  const [states, setStates] = useMultipleStateDebounce(initialStates, delay);
  const pendingStatesRef = useRef(initialStates);

  const filteredStates = useMemo(
    () => getFilteredFromStates(states, defaultStatesRef.current),
    [states],
  );

  useEffect(() => {
    setOldFilters(filteredStates);
  }, [filteredStates]);

  const handleSetStates = (key: string, value?: any) => {
    let newStates: any = null;
    if (key === 'cancelall') {
      value = value || {};
      newStates = { ...defaultStates, ...importantStates, ...value };
      pendingStatesRef.current = newStates;
      setStates(newStates, true);
    } else if (key === 'all') {
      newStates = { ...value };
      pendingStatesRef.current = newStates;
      setStates(newStates, true);
    } else if (key === 'force') {
      newStates = { ...pendingStatesRef.current, ...value };
      pendingStatesRef.current = newStates;
      setStates(newStates, true);
    } else {
      newStates = { ...pendingStatesRef.current, [key]: value };
      pendingStatesRef.current = newStates;
      setStates(newStates);
    }
  };

  return [states, handleSetStates, filteredStates];
};
